import React, { useCallback, memo } from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
	useNavigate,
	useLocation,
} from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginScreen from "./components/auth/LoginScreen";
import Dashboard from "./components/Dashboard";
import AuthCallback from "./components/auth/AuthCallback";
import PrivacyPolicyScreen from "./components/layout/PrivacyPolicyScreen";
import { LoadingScreen } from "./components/common/LoadingSpinner";

// RequireAuth component remains the same
const RequireAuth = memo(({ children }) => {
	const { isAuthenticated, loading } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	const checkAuth = useCallback(() => {
		if (!loading && !isAuthenticated) {
			const returnTo = location.pathname + location.search;
			navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
				replace: true,
			});
		}
	}, [isAuthenticated, loading, navigate, location]);

	React.useEffect(() => {
		checkAuth();
	}, [checkAuth]);

	if (loading) {
		return <LoadingScreen />;
	}

	return isAuthenticated ? children : null;
});

RequireAuth.displayName = "RequireAuth";

// Updated AppRoutes with Privacy Policy
const AppRoutes = memo(() => (
  <Routes>
    <Route path="/login" element={<LoginScreen />} />
    <Route path="/auth/callback" element={<AuthCallback />} />
    <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} /> {/* Public route */}
    <Route
      path="/dashboard/*"
      element={
        <RequireAuth>
          <Dashboard />
        </RequireAuth>
      }
    />
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>
));

AppRoutes.displayName = 'AppRoutes';

// AppProviders remains the same
const AppProviders = ({ children }) => (
	<ThemeProvider>
		<AuthProvider>
			<Router>{children}</Router>
		</AuthProvider>
	</ThemeProvider>
);

function App() {
	return (
		<AppProviders>
			<AppRoutes />
		</AppProviders>
	);
}

export default App;
